/**
 * Zerion API Service
 *
 * Single integration point for fetching wallet portfolio data.
 *
 * Architecture:
 *   Browser → Cloudflare Worker proxy → Zerion API → back
 *
 *   Direct browser → Zerion calls are CORS-blocked. The Worker
 *   (arc-zerion-proxy.arc-account-1e8.workers.dev) lives at the edge,
 *   forwards requests to Zerion server-side (where CORS doesn't apply),
 *   and returns the response with proper CORS headers. The Worker also
 *   holds the Zerion API key as a Cloudflare secret — frontend never
 *   sees the key, eliminating the credential-in-bundle risk.
 *
 * Configuration:
 *   - REACT_APP_ZERION_PROXY_URL: optional override (e.g., for staging).
 *     Defaults to the production Worker URL below.
 *   - If proxy URL is unreachable, service runs in MOCK MODE for dev.
 *
 * Rate-limit strategy:
 *   - 30-min in-memory cache per address keyed on `${address}_${chain}`.
 *   - 'Refresh' from UI bypasses cache (force=true).
 *   - On 429 (rate limit), surface a structured error so UI can show a friendly
 *     "Try again in a minute" message instead of breaking.
 */

const ZERION_BASE =
  process.env.REACT_APP_ZERION_PROXY_URL ||
  "https://arc-zerion-proxy.arc-account-1e8.workers.dev";

// HAS_KEY now means "is the proxy configured." The frontend doesn't hold
// the actual Zerion API key anymore — the Worker does. Kept the variable
// name to avoid touching every call site.
const HAS_KEY = Boolean(ZERION_BASE && ZERION_BASE.startsWith("http"));
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

const cache = new Map();

/* ─── Cache helpers ────────────────────────────────────────────────────── */
const cacheKey = (address, kind) => `${kind}:${address.toLowerCase()}`;

const getCached = (key) => {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.data;
};

const setCached = (key, data) => {
  cache.set(key, { data, fetchedAt: Date.now() });
};

/* ─── Mock data (when no API key) ──────────────────────────────────────── */
const mockPortfolio = (address) => {
  // Deterministic mock — same address always returns same value so the UI
  // doesn't flicker during development.
  let seed = 0;
  for (let i = 0; i < address.length; i++) seed = (seed + address.charCodeAt(i)) % 100000;
  const total = 5000 + (seed % 50000);
  return {
    totalValue: total,
    chains: {
      ethereum: total * 0.4,
      base: total * 0.2,
      arbitrum: total * 0.15,
      polygon: total * 0.1,
      optimism: total * 0.15,
    },
    isMock: true,
    fetchedAt: new Date().toISOString(),
  };
};

const mockPositions = (address) => {
  const portfolio = mockPortfolio(address);
  return {
    positions: [
      {
        protocol: "Enzyme",
        type: "vault",
        name: "Sample Enzyme Vault",
        value: portfolio.totalValue * 0.3,
        chain: "ethereum",
      },
      {
        protocol: "Aave",
        type: "lending",
        name: "USDC Supply",
        value: portfolio.totalValue * 0.2,
        chain: "ethereum",
      },
      {
        protocol: "Wallet",
        type: "tokens",
        name: "Token Balances",
        value: portfolio.totalValue * 0.5,
        chain: "ethereum",
      },
    ],
    isMock: true,
    fetchedAt: new Date().toISOString(),
  };
};

/* ─── Real API calls ───────────────────────────────────────────────────── */
const apiFetch = async (path, params = {}) => {
  const url = new URL(`${ZERION_BASE}${path}`);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) url.searchParams.set(k, v);
  });

  // Auth lives on the Worker side now — frontend just makes the request.
  const res = await fetch(url.toString(), {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
  });

  if (res.status === 429) {
    const err = new Error("Zerion rate limit reached. Try again in a minute.");
    err.code = "RATE_LIMIT";
    throw err;
  }

  if (res.status === 401 || res.status === 403) {
    const err = new Error(
      "Zerion API key is invalid or missing required permissions."
    );
    err.code = "AUTH";
    throw err;
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(
      `Zerion API error: ${res.status} ${res.statusText} ${text.slice(0, 200)}`
    );
    err.code = "HTTP_" + res.status;
    throw err;
  }

  return res.json();
};

/* ─── Public: fetchPortfolio ───────────────────────────────────────────── */
/**
 * Returns the aggregated portfolio total for an address across all chains.
 *
 *   { totalValue: number, chains: { [chainId]: number }, isMock?: bool, fetchedAt: ISO }
 */
export async function fetchPortfolio(address, { force = false } = {}) {
  if (!address) throw new Error("Address is required");

  const key = cacheKey(address, "portfolio");
  if (!force) {
    const cached = getCached(key);
    if (cached) return { ...cached, fromCache: true };
  }

  if (!HAS_KEY) {
    const mock = mockPortfolio(address);
    setCached(key, mock);
    return mock;
  }

  // Real Zerion call
  // /v1/wallets/{address}/portfolio?currency=usd&filter[positions]=no_filter
  const data = await apiFetch(`/v1/wallets/${address.toLowerCase()}/portfolio`, {
    currency: "usd",
    "filter[positions]": "no_filter",
    sync: force ? true : false,
  });

  // Log raw response for debugging — open browser console to see this.
  // Remove or guard with a debug flag once values look correct.
  // eslint-disable-next-line no-console
  console.log(
    `[Zerion] /portfolio for ${address}:\n` + JSON.stringify(data, null, 2)
  );

  const attrs = data?.data?.attributes || {};

  // Zerion splits totals across "positions_distribution_by_type":
  //   wallet     — fungible tokens sitting in wallet
  //   deposited  — supplied to lending
  //   borrowed   — debt (negative)
  //   locked     — vested/locked positions
  //   staked     — staked positions
  // The sum of all these IS the true total. `total.positions` alone often
  // omits wallet token balances, which causes badly low totals.
  const byType = attrs?.positions_distribution_by_type || {};
  const sumByType = Object.values(byType).reduce(
    (s, v) => s + (typeof v === "number" ? v : 0),
    0
  );

  // Use Zerion's authoritative total — this is the sum-of-truth across
  // ALL position types and chains. The `positions_distribution_by_type`
  // sometimes excludes a few categorized-only-as-total items (yield, rewards),
  // so summing it loses ~1%. `total.positions` is always the right number
  // when `filter[positions]=no_filter` is passed in the request.
  const totalFromTotal = attrs?.total?.positions || 0;
  const totalValue = totalFromTotal > 0 ? totalFromTotal : sumByType;

  const chains = attrs?.positions_distribution_by_chain || {};

  const result = {
    totalValue,
    chains,
    breakdown: byType,
    fetchedAt: new Date().toISOString(),
  };

  setCached(key, result);
  return result;
}

/* ─── Public: fetchPositions ───────────────────────────────────────────── */
/**
 * Returns DeFi positions and token holdings for an address.
 *
 *   { positions: [{ protocol, type, name, value, chain }], fetchedAt, isMock? }
 */
export async function fetchPositions(address, { force = false } = {}) {
  if (!address) throw new Error("Address is required");

  const key = cacheKey(address, "positions");
  if (!force) {
    const cached = getCached(key);
    if (cached) return { ...cached, fromCache: true };
  }

  if (!HAS_KEY) {
    const mock = mockPositions(address);
    setCached(key, mock);
    return mock;
  }

  // /v1/wallets/{address}/positions/?filter[positions]=only_complex&currency=usd
  const data = await apiFetch(`/v1/wallets/${address}/positions/`, {
    "filter[positions]": "no_filter",
    currency: "usd",
    sync: force ? true : false,
  });

  const items = Array.isArray(data?.data) ? data.data : [];
  const positions = items.map((p) => {
    const a = p.attributes || {};
    return {
      protocol: a.protocol || "Wallet",
      type: a.position_type || "unknown",
      name: a.name || "",
      value: a.value || 0,
      chain: a?.relationships?.chain?.data?.id || "ethereum",
    };
  });

  const result = {
    positions,
    fetchedAt: new Date().toISOString(),
  };

  setCached(key, result);
  return result;
}

/* ─── Public: status helpers ───────────────────────────────────────────── */
export const isConfigured = () => HAS_KEY;

export const clearCache = () => cache.clear();

export default {
  fetchPortfolio,
  fetchPositions,
  isConfigured,
  clearCache,
};
